import { DropdownMenu, DropdownMenuTrigger, Item as DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { AccountIcon, Button, Close, DropdownMenuContent, Logout, Title } from '@components';
import { useLogout } from '@services/shopify/operations/logout';
import { useIsMobile, useTranslation } from '@utils/hooks';
import { EXTRA_SMALL_WIDTH, LOCALE_CODES } from '@constants';
import styles from './AccountDropdown.module.scss';

const getTranslatedTexts = translator => {
	return {
		accountMenu: translator('account-menu'),
		welcomeCustomer: (customerName: string) => translator('welcome-customer', { customerName }),
		myFavorites: translator('my-favorites'),
		myCollection: translator('my-collection'),
		myOrders: translator('my-orders'),
		myRewards: translator('my-rewards'),
		mySubscriptions: translator('my-subscriptions'),
		myMembership: translator('my-membership'),
		logOut: translator('log-out'),
	};
};

const AccountDropdown = ({ customer }) => {
	const logout = useLogout();
	const isMobile = useIsMobile({ maxWidth: EXTRA_SMALL_WIDTH });
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
	const { locale } = useRouter();
	const showSubscription = locale === LOCALE_CODES.US;

	const userId = customer?.id?.split('/').pop();
	const storeUrl = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN;
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	const membershipUrl = `https://${storeUrl}/apps/subscribfy-api/store/manage-membership-dashboard?cid=${userId}&exm=1&hash=${customer?.membershipHash}`;

	const toggleDropdown = open => {
		// Workaround to prevent the scroll bar from disappearing when the dropdown is open
		// Since the dropdown menu uses react-remove-scroll under the hood, based on:
		// https://github.com/radix-ui/primitives/discussions/1100
		if (open) {
			document.body.style.setProperty('overflow', 'auto', 'important');
			document.body.style.setProperty('margin-right', '0', 'important');
		} else {
			document.body.style.removeProperty('overflow');
			document.body.style.removeProperty('margin-right');
		}
		setDropdownIsOpen(!dropdownIsOpen);
	};

	return (
		<DropdownMenu open={dropdownIsOpen} onOpenChange={toggleDropdown}>
			<DropdownMenuTrigger data-nav-icon='account' data-test={customer ? 'account' : 'login'}>
				<div onMouseEnter={toggleDropdown} className={styles['nav-icon']}>
					<AccountIcon loggedIn={!!customer} label='account' />
				</div>
			</DropdownMenuTrigger>

			<DropdownMenuContent title={translations.accountMenu} align={isMobile ? 'center' : 'end'}>
				<div className={styles.container} onMouseLeave={toggleDropdown}>
					<header>
						<Title>{translations.welcomeCustomer(customer.firstName)}</Title>
						<Close label='x' onClick={toggleDropdown} />
					</header>
					<ul>
						<li>
							<DropdownMenuItem className={styles.dropdownItem}>
								<Link href={'/account/favorites'}>
									<Button
										label={translations.myFavorites}
										size='large'
										color='white'
										padding='1rem 2.4rem'
										withChevron
										chevronDirection='right'
										removeEffects
										fullWidth
										border='none'
										spaceBetween
									/>
								</Link>
							</DropdownMenuItem>
						</li>
						<li>
							<DropdownMenuItem className={styles.dropdownItem}>
								<Link href={'/account/collection'}>
									<Button
										label={translations.myCollection}
										size='large'
										color='white'
										padding='1rem 2.4rem'
										withChevron
										chevronDirection='right'
										removeEffects
										fullWidth
										border='none'
										spaceBetween
									/>
								</Link>
							</DropdownMenuItem>
						</li>
						<li>
							<DropdownMenuItem className={styles.dropdownItem}>
								<Link href={'/account/orders'}>
									<Button
										label={translations.myOrders}
										size='large'
										color='white'
										padding='1rem 2.4rem'
										withChevron
										chevronDirection='right'
										removeEffects
										fullWidth
										border='none'
										spaceBetween
									/>
								</Link>
							</DropdownMenuItem>
						</li>
						<li>
							<DropdownMenuItem className={styles.dropdownItem}>
								{/* Quick fix to redirect the user to /rewards until the complete refactor */}
								<Link href={'/rewards'}>
									<Button
										label={translations.myRewards}
										size='large'
										color='white'
										padding='1rem 2.4rem'
										withChevron
										chevronDirection='right'
										removeEffects
										fullWidth
										border='none'
										spaceBetween
									/>
								</Link>
							</DropdownMenuItem>
						</li>
						{customer?.subscriberId && showSubscription && (
							<li>
								<DropdownMenuItem className={styles.dropdownItem}>
									<Link href={'/account/subscriptions'}>
										<Button
											label={translations.mySubscriptions}
											size='large'
											color='white'
											padding='1rem 2.4rem'
											withChevron
											chevronDirection='right'
											removeEffects
											fullWidth
											border='none'
											spaceBetween
										/>
									</Link>
								</DropdownMenuItem>
							</li>
						)}
						{customer.isMembershipVip && showSubscription && (
							<li>
								<DropdownMenuItem className={styles.dropdownItem}>
									<Link target='_blank' href={membershipUrl}>
										<Button
											label={translations.myMembership}
											size='large'
											color='white'
											padding='1rem 2.4rem'
											withChevron
											chevronDirection='right'
											removeEffects
											fullWidth
											border='none'
											spaceBetween
										/>
									</Link>
								</DropdownMenuItem>
							</li>
						)}
						<li>
							<DropdownMenuItem className={styles.dropdownItem}>
								<Button
									size='large'
									color='white'
									padding='1rem 2.4rem'
									spaceBetween
									removeEffects
									fullWidth
									border='none'
									onClick={logout}
								>
									{translations.logOut}
									<Logout />
								</Button>
							</DropdownMenuItem>
						</li>
					</ul>
				</div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default AccountDropdown;
